import { useState, type ReactElement } from "react";

import type { BacklogItemView } from "../api/client.js";

/**
 * Ideas: somewhere to drop a thought the moment it arrives, without opening a
 * conversation. Worked through when there is spare capacity; anything urgent
 * still goes straight to Claude.
 */
const STATUSES = ["OPEN", "DOING", "DONE", "DROPPED"] as const;

export function IdeasPage({
  items = [],
  onAdd,
  onStatus,
}: {
  readonly items?: readonly BacklogItemView[];
  readonly onAdd?: (input: { title: string; note: string | null }) => void;
  readonly onStatus?: (id: string, status: BacklogItemView["status"]) => void;
}): ReactElement {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const open = items.filter((item) => item.status === "OPEN").length;

  return (
    <div className="board-grid">
      {onAdd === undefined ? null : (
        <form
          className="card idea-form"
          aria-label="Log an idea"
          onSubmit={(event) => {
            event.preventDefault();
            if (title.trim() === "") return;
            onAdd({
              title: title.trim(),
              note: note.trim() === "" ? null : note.trim(),
            });
            setTitle("");
            setNote("");
          }}
        >
          <div className="section-heading">
            <h3>Log an idea</h3>
            <span>{open} open</span>
          </div>
          <label>
            <span>Idea</span>
            <input
              type="text"
              maxLength={200}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="One line"
            />
          </label>
          <label>
            <span>Detail</span>
            <textarea
              rows={3}
              maxLength={2000}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Optional"
            />
          </label>
          <button
            className="button primary"
            type="submit"
            disabled={title.trim() === ""}
          >
            Add
          </button>
        </form>
      )}
      <ul className="idea-list">
        {items.length === 0 ? (
          <li className="card muted">Nothing logged yet.</li>
        ) : (
          items.map((item) => (
            <li
              key={item.id}
              className={`card idea idea-${item.status.toLowerCase()}`}
            >
              <div className="idea-head">
                <strong>{item.title}</strong>
                {onStatus === undefined ? (
                  <span className="idea-status">{item.status}</span>
                ) : (
                  <select
                    aria-label={`Status of ${item.title}`}
                    value={item.status}
                    onChange={(event) =>
                      onStatus(
                        item.id,
                        event.target.value as BacklogItemView["status"],
                      )
                    }
                  >
                    {STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status.charAt(0) + status.slice(1).toLowerCase()}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {item.note === null ? null : <p>{item.note}</p>}
              {item.resolutionNote === null ? null : (
                <p className="idea-resolution">{item.resolutionNote}</p>
              )}
              <time className="muted" dateTime={item.createdAt}>
                {new Intl.DateTimeFormat("en-AU", {
                  timeZone: "Australia/Melbourne",
                  dateStyle: "medium",
                }).format(new Date(item.createdAt))}
              </time>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
